import { useOidc } from '@axa-fr/react-oidc';
import { AtlasColumns, AtlasFieldCheckboxGroup, AtlasFieldPeriod, AtlasFieldText, AtlasFlex, AtlasSpacer, CampusFilters, CampusPagination } from "atlas-ds";
import { addYears, endOfTomorrow, startOfTomorrow } from "date-fns";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { EntrepriseContext } from "pages/_app";
import { RefObject, useContext, useEffect, useRef, useState } from "react";
import { fetchAPIDomaines, fetchAPILieux, fetchAPIModules, fetchAPIOrganismes, fetchAPIThematiques } from "services/client";
import { BRANCHE_CAP, CAP_COMPETENCE_URL, MODALITES_TOOLTIPS, OPTIONS_TOOLTIPS, TYPES_TOOLTIPS } from 'services/global';
import { Branche, Domaine, Lieux, Modalite, Module, Organisme, SearchContext, Thematique, Type } from "services/model";
import { useOidcSession } from "services/useOidcSession";
import { formatVille, pathify } from "utils/format";
import useDebounce from "utils/useDebounce";
import { Dropdown } from "./dropdown";

interface FiltersProps {
    children: JSX.Element
    pageSize: number
    branches: Branche[]
    modalites: Modalite[]
    types: Type[]
    updateFilters: (filters: any, replace?: boolean) => void
    count?: number
    context?: SearchContext
    searchInputRef?: RefObject<HTMLInputElement>
}

export function Filters(props: FiltersProps) {
    const { data: authSession, status: authenticating } = useOidcSession()
    const { login } = useOidc()
    const searchParams = useSearchParams()
    const router = useRouter()
    const [entreprise] = useContext(EntrepriseContext)

    const [recherche, setRecherche, _, rechercheDebounced, forceRecherche]
        = useDebounce(searchParams?.get('recherche') ?? "", 300)

    const [lieux, setLieux] = useState<Lieux>()
    const [domaines, setDomaines] = useState<Domaine[]>()
    const [thematiques, setThematiques] = useState<Thematique[]>()
    const [modules, setModules] = useState<Module[]>()
    const [organismes, setOrganismes] = useState<Organisme[]>()

    const scrollToTopRef = useRef<HTMLDivElement>(null)

    const getBrancheParam = () => props.context?.organisme
        ? (searchParams?.get("branche") ?? undefined)
        : (props.context?.brancheId ?? searchParams?.get("branche") ?? undefined)

    useEffect(() => {
        if (
            rechercheDebounced === undefined
            || rechercheDebounced?.length === 0
            || (rechercheDebounced?.length ?? 0) >= 2
        ) {
            props.updateFilters({ recherche: rechercheDebounced })
        }
    }, [rechercheDebounced])

    useEffect(() => {
        forceRecherche(searchParams?.get("recherche") ?? "")
    }, [searchParams?.get("recherche")])

    useEffect(() => {
        if (
            (authenticating === "authenticated" && entreprise?.branche?.valueName)
            || authenticating === "unauthenticated"
        ) {
            // Récupérer les domaines
            if ((props.context?.brancheId ?? searchParams?.get("branche")) || searchParams?.get("domaine")) {
                fetchAPIDomaines({
                    brancheId: getBrancheParam(),
                    organisme: props.context?.organisme
                }).then(domaines => {
                    setDomaines(domaines)
                    
                    // Retirer le domaine de l'URL s'il ne fait pas partie des domaines disponibles
                    if (
                        searchParams?.get("domaine")
                        && domaines
                        && !domaines.find(domaine => domaine.sfid === searchParams.get("domaine"))
                    ) {
                        props.updateFilters({domaine: undefined, thematique: undefined, module: undefined}, true)
                    }
                })
            } else {
                setDomaines(undefined)
                setThematiques(undefined)
                setModules(undefined)
            }

            // Récupérer les organismes pour la branche sélectionnée (sauf si l'organisme est défini)
            if (!props.context?.organisme) {
                fetchAPIOrganismes({
                    brancheId: props.context?.brancheId,
                }).then(organismes => setOrganismes(organismes))
            }

            // Récupérer les lieux pour la branche sélectionnée
            fetchAPILieux({
                brancheId: (props.context?.stage || props.context?.organisme)
                    ? undefined
                    : props.context?.brancheId,
                stage: props.context?.stage,
                organisme: props.context?.organisme,
            }).then(lieux => setLieux(lieux))
        }
    }, [authenticating, entreprise?.branche?.valueName, searchParams?.get("branche")])

    useEffect(() => {
        if (searchParams?.get("domaine")) {
            // Récupérer les thématiques pour le domaine sélectionné
            fetchAPIThematiques({
                brancheId: getBrancheParam(),
                domaineId: searchParams?.get("domaine") as string
            }).then(thematiques => {
                setThematiques(thematiques)

                // Retirer la thématique de l'URL si elle ne fait pas partie des thématiques disponibles
                if (
                    searchParams?.get("thematique")
                    && thematiques
                    && !thematiques.find(thematique => thematique.sfid === searchParams.get("thematique"))
                ) {
                    props.updateFilters({thematique: undefined, module: undefined}, true)
                }
            })
        } else {
            setThematiques(undefined)
            setModules(undefined)
        }
    }, [searchParams?.get("domaine")])

    useEffect(() => {
        if (searchParams?.get("thematique")) {
            // Récupérer les modules pour la thématique sélectionnée
            fetchAPIModules({
                brancheId: getBrancheParam(),
                domaineId: searchParams?.get("domaine") as string,
                thematiqueId: searchParams?.get("thematique") as string
            }).then(modules => {
                setModules(modules)

                // Retirer le module de l'URL s'il ne fait pas partie des modules disponibles
                if (
                    searchParams?.get("module")
                    && modules
                    && !modules.find(module => module.sfid === searchParams.get("module"))
                ) {
                    props.updateFilters({module: undefined}, true)
                }
            })
        } else {
            setModules(undefined)
        }
    }, [searchParams?.get("thematique")])

    useEffect(() => {
        const updates: any = {}

        // Retirer l'organisme de l'URL s'il ne fait pas partie des organismes disponibles
        if (
            searchParams?.get("organisme")
            && organismes
            && !organismes.find(organisme => organisme.sfid === searchParams.get("organisme"))
        ) {
            updates.organisme = undefined
        }

        // Retirer la région de l'URL si elle ne fait pas partie des régions disponibles
        if (
            searchParams?.get("region")
            && lieux?.regions
            && !lieux.regions.find((region) => region.sfid === searchParams.get("region"))
        ) {
            updates.region = undefined
        }

        // Retirer la ville de l'URL si elle ne fait pas partie des villes disponibles
        if (
            searchParams?.get("ville")
            && lieux?.villes
            && !lieux.villes.find((ville) => `${ville.ville}:${ville.codeDepartement}` === searchParams.get("ville"))
        ) {
            updates.ville = undefined
        }

        if (Object.keys(updates).length > 0) {
            props.updateFilters(updates, true)
        }

    }, [searchParams, thematiques, modules, organismes, lieux])

    // Changer de page
    const onPageSelect = (page: number) => {
        const topIsInView = scrollToTopRef.current && scrollToTopRef.current.getBoundingClientRect().bottom >= -32

        if (!topIsInView) {
            scrollToTopRef.current?.scrollIntoView()
        }

        // 700 is approximately the time it takes Chrome to scroll to the top
        setTimeout(() => props.updateFilters({page: page === 0 ? undefined : page + 1}), topIsInView ? 0 : 700)
    }

    // Supprimer ou mettre à jour la branche dans les paramètres URLs
    const updateBrancheInParams = (newValue: string | null, setNewValueInParams = true) => {
        const params = new URLSearchParams(searchParams!)

        if (newValue && setNewValueInParams) {
            params.set("branche", newValue)
        } else {
            params.delete("branche")
        }

        // Si c'est une suppression de branche ou si le domaine sélectionné n'existe pas dans la branche d'arrivée,
        // on supprime le domaine, la thématique et le module
        if (
            !newValue
            || (params.get("domaine") && !domaines?.find(
                    domaine => domaine.sfid === searchParams?.get("domaine")
                    && domaine.branches?.includes(newValue)
            ))
        ) {
            params.delete("domaine")
            params.delete("thematique")
            params.delete("module")
        }

        return params.toString()
    }

    const onSelectBranche = (branche: Branche) => {
        if (router.pathname === "/" && branche.valueName === BRANCHE_CAP) {
            // Navigation de la homepage vers la page CAP
            router.push(
                { pathname: CAP_COMPETENCE_URL, query: updateBrancheInParams(BRANCHE_CAP, false) },
                undefined,
                { shallow: true }
            )
        } else if (router.pathname === CAP_COMPETENCE_URL && branche.valueName !== BRANCHE_CAP) {
            // Navigation de la page CAP vers la homepage
            router.push(
                { pathname: '/', query: updateBrancheInParams(branche.valueName) },
                undefined,
                { shallow: true }
            )
        } else {
            // Navigation simple (restant sur la homepage)
            props.updateFilters({
                branche: branche.valueName,
                ...(searchParams?.get("domaine") && !domaines?.find(
                    domaine => domaine.sfid === searchParams?.get("domaine")
                    && domaine.branches?.includes(branche.valueName)
                ))
                    ? { domaine: undefined, thematique: undefined, module: undefined }
                    : undefined,
            })
        }
    }

    const selectedBranche = router.pathname === CAP_COMPETENCE_URL
        ? BRANCHE_CAP
        : entreprise?.branche?.valueName && !props.context?.organisme
            ? entreprise?.branche?.valueName
            : searchParams?.get("branche")

    const onClearBranche = () => {
        if (router.pathname === CAP_COMPETENCE_URL) {
            // Supprimer la branche ramène vers l'URL racine si l'on est sur cap compétence
            router.push(
                { pathname: '/', query: updateBrancheInParams(null) },
                undefined,
                { shallow: true }
            )
        } else {
            props.updateFilters({
                branche: undefined,
                domaine: undefined,
                thematique: undefined,
                module: undefined
            })
        }
    }

    return <div>
        <AtlasColumns reversed>
            <AtlasFlex column gap="m">
                { !props.context?.stage && <CampusFilters>
                    <AtlasFieldText
                        ref={ props.searchInputRef }
                        value={ recherche ?? '' }
                        icon="recherche"
                        name="recherche"
                        label="Recherche"
                        instructions='2 caractères minimum'
                        onChange={ event => setRecherche(event.target.value) }
                        onClear={ () => forceRecherche("") }
                    />
                </CampusFilters> }

                {!props.context?.stage && <>
                    <CampusFilters>
                        {!props.context?.organisme && !authSession && <CampusFilters.Instructions>
                            <strong>Personnalisez votre recherche</strong>
                            <button onClick={() => login('login')}>en vous connectant à myAtlas</button>{" "}
                            ou en renseignant votre branche professionnelle&nbsp;:
                        </CampusFilters.Instructions>}

                        <CampusFilters.Funnel>
                            {<CampusFilters.Highlight active={!selectedBranche && !props.context?.organisme}>
                                <Dropdown
                                    name="branche"
                                    label="Branche professionnelle"
                                    icon="filtres"
                                    options={ props.branches?.map(branche => ({
                                        id: branche.valueName,
                                        content: branche.label,
                                        selected: branche.valueName === selectedBranche,
                                        onSelect: () => onSelectBranche(branche)
                                    })) ?? []}
                                    onClear={onClearBranche}
                                    tooltipContent={ (!props.context?.organisme && !!entreprise?.branche?.valueName)
                                        ? "La branche est automatiquement récupérée à partir de votre entreprise. Si votre compte est associé à plusieurs entreprises, sélectionnez celle de votre choix en haut de la page."
                                        : undefined
                                    }
                                    disabled={ !props.context?.organisme && !!entreprise?.branche?.valueName }
                                />
                            </CampusFilters.Highlight>}
                            {(selectedBranche || searchParams?.get("domaine")) && <Dropdown
                                name="domaine"
                                label="Domaine"
                                icon="domaine"
                                options={ (domaines ?? []).map((domaine) => ({
                                    id: domaine.sfid,
                                    content: domaine.label,
                                    selected: domaine.sfid === searchParams?.get("domaine"),
                                    onSelect: () => props.updateFilters({
                                        domaine: domaine.sfid,
                                        thematique: undefined,
                                        module: undefined
                                    })
                                }))}
                                onClear={() => props.updateFilters({
                                    domaine: undefined,
                                    thematique: undefined,
                                    module: undefined
                                })}
                                disabled={ !selectedBranche && !searchParams?.get("domaine") }
                                loading={ !(!searchParams?.get("branche") && !entreprise?.branche?.valueName) && !domaines }
                            />}
                            { searchParams?.get("domaine") && <Dropdown
                                name="thematique"
                                label="Thématique"
                                icon="thematique"
                                options={ (thematiques ?? []).map((thematique) => ({
                                    id: thematique.sfid,
                                    content: thematique.label,
                                    selected: thematique.sfid === searchParams?.get("thematique"),
                                    onSelect: () => props.updateFilters({
                                        thematique: thematique.sfid,
                                        module: undefined
                                    })
                                })) ?? [] }
                                onClear={() => props.updateFilters({ thematique: undefined, module: undefined })}
                                disabled={ !searchParams?.get("domaine") }
                                loading={ !!searchParams.get("domaine") && !thematiques }
                            /> }
                            { searchParams?.get("thematique") && <Dropdown
                                name="module"
                                label="Module"
                                icon="module"
                                onClear={() => props.updateFilters({ module: undefined })}
                                options={ (modules ?? []).map((module) => ({
                                    id: module.sfid,
                                    content: module.label,
                                    selected: module.sfid === searchParams?.get("module"),
                                    onSelect: () => props.updateFilters({ module: module.sfid })
                                })) ?? [] }
                                disabled={ !searchParams?.get("thematique") }
                                loading={ !!searchParams.get("thematique") && !modules }
                            /> }
                        </CampusFilters.Funnel>

                        { !props.context?.organisme && <CampusFilters.Instructions>
                            <strong>Besoin d'une vision d'ensemble&nbsp;?</strong> Consultez le <Link href={entreprise?.branche
                                ? `/branches/${pathify(entreprise?.branche?.label)}/${entreprise?.branche?.valueName}`
                                : "/branches"
                            }>catalogue des formations</Link>.
                        </CampusFilters.Instructions> }
                    </CampusFilters>
                </>}

                <CampusFilters>
                    { !props.context?.stage && <Dropdown
                        icon="batiment"
                        name="organisme"
                        label="Organisme"
                        onClear={() => props.updateFilters({ organisme: undefined })}
                        options={ organismes?.map((organisme) => ({
                            id: organisme.sfid,
                            content: organisme.name,
                            selected: organisme.sfid === searchParams?.get("organisme"),
                            onSelect: () => props.updateFilters({ organisme: organisme.sfid })
                        })) ?? []}
                        loading={ !props.context?.organisme && !organismes }
                        disabled={ !!props.context?.organisme }
                        recherche={ props.context?.organisme?.name }
                    /> }
                    <AtlasFieldPeriod
                        name="periode"
                        label="Période"
                        min={startOfTomorrow()}
                        max={addYears(endOfTomorrow(), 1)}
                        value={ searchParams?.get("date-debut") && searchParams?.get("date-fin")
                            ? [
                                new Date(searchParams?.get("date-debut") ?? ''),
                                new Date(searchParams?.get("date-fin") ?? '')]
                            : []
                         }
                        onPeriodChange={ ([dateDebut, dateFin]) => props.updateFilters(
                            { "date-debut": dateDebut, "date-fin": dateFin }
                        )}
                        onClear={ () => props.updateFilters({ "date-debut": undefined, "date-fin": undefined })}
                    />
                    <Dropdown
                        icon="carte"
                        name="lieu"
                        label="Région ou ville"
                        loading={!lieux}
                        disabled={lieux && lieux.regions.length === 0 && lieux.villes.length === 0}
                        onClear={() => props.updateFilters({ region: undefined, ville: undefined })}
                        options={[
                            ...lieux?.regions.length ? [{
                                id: "regions",
                                label: "Régions",
                                options: lieux.regions.map((region) => ({
                                    id: region.sfid,
                                    content: region.region,
                                    selected: region.sfid === searchParams?.get("region"),
                                    onSelect: () => props.updateFilters({ region: region.sfid, ville: undefined }),
                                }))
                            }] : [],
                            ...lieux?.villes.length ? [{
                                id: "villes",
                                label: "Villes",
                                options: lieux.villes.map((ville) => ({
                                    id: `${ville.ville}:${ville.codeDepartement}`,
                                    content: `${formatVille(ville.ville)} (${ville.codeDepartement})`,
                                    selected: `${ville.ville}:${ville.codeDepartement}` === searchParams?.get("ville"),
                                    onSelect: () => props.updateFilters({
                                        region: undefined,
                                        ville: `${ville.ville}:${ville.codeDepartement}`
                                    })
                                }))
                            }] : []
                        ]}
                    />
                    <AtlasFieldCheckboxGroup
                        name="modalite"
                        label="Modalités pédagogiques"
                        options={props.modalites?.sort((a, b) => a.label > b.label ? 1 : -1).map(modalite => ({
                            label: modalite.label,
                            value: modalite.valueName,
                            checked: searchParams?.getAll("modalite").includes(modalite.valueName),
                            tooltipContent: MODALITES_TOOLTIPS.get(modalite.valueName)
                        })) ?? []}
                        onChange={ event => props.updateFilters({ modalite: event.target.checked
                            ? [...searchParams?.getAll("modalite") ?? [], event.target.value]
                            : searchParams?.getAll("modalite").filter((modalite: string) => modalite !== event.target.value)
                        }) }
                    />
                    <AtlasFieldCheckboxGroup
                        name="type"
                        label="Type de formation"
                        options={props.types?.map(type => ({
                            label: type.label,
                            value: type.valueName,
                            checked: searchParams?.getAll("type").includes(type.valueName),
                            tooltipContent: TYPES_TOOLTIPS.get(type.valueName)
                        })) ?? []}
                        onChange={ event => props.updateFilters({ type: event.target.checked
                            ? [...searchParams?.getAll("type") ?? [], event.target.value]
                            : searchParams?.getAll("type")!.filter((type: string) => type !== event.target.value)
                        }) }
                    />
                    <AtlasFieldCheckboxGroup
                        name="options"
                        label="Options"
                        options={[
                            {
                                label: "Cofinancement",
                                value: "cofinancement",
                                checked: !!searchParams?.get("cofinancement"),
                                tooltipContent: OPTIONS_TOOLTIPS.get("cofinancement"),
                                onChange: (event) => props.updateFilters({ "cofinancement": event.target.checked })
                            },
                            {
                                label: "DDA compatible",
                                value: "dda-compatible",
                                checked: !!searchParams?.get("dda-compatible"),
                                tooltipContent: OPTIONS_TOOLTIPS.get("dda-compatible"),
                                onChange: (event) => props.updateFilters({ "dda-compatible": event.target.checked })
                            },
                            {
                                label: "Formation certifiante",
                                value: "formation-certifiante",
                                checked: !!searchParams?.get("formation-certifiante"),
                                tooltipContent: OPTIONS_TOOLTIPS.get("formation-certifiante"),
                                onChange: (event) => props.updateFilters({ "formation-certifiante": event.target.checked })
                            },
                            {
                                label: "Libre accès",
                                value: "libre-acces",
                                checked: !!searchParams?.get("libre-acces"),
                                tooltipContent: OPTIONS_TOOLTIPS.get("libre-acces"),
                                onChange: (event) => props.updateFilters({ "libre-acces": event.target.checked })
                            },
                            {
                                label: "Session garantie",
                                value: "session-garantie",
                                checked: !!searchParams?.get("session-garantie"),
                                tooltipContent: OPTIONS_TOOLTIPS.get("session-garantie"),
                                onChange: (event) => props.updateFilters({ "session-garantie": event.target.checked })
                            }
                        ]}
                    />
                </CampusFilters>
            </AtlasFlex>

            <div>
                <div ref={scrollToTopRef} style={{scrollMarginBlockStart: "2rem"}}></div>

                {props.children}

                <AtlasSpacer size="xxl" />

                {((props.count ?? 0) > 0) && <CampusPagination
                    pageCount={Math.ceil(props.count! / props.pageSize)}
                    currentPage={ parseInt(searchParams?.get("page") ?? "1") - 1 }
                    onPageSelect={ onPageSelect }
                />}
            </div>
        </AtlasColumns>
    </div>
}
